import axiosInstance from '~/common/helpers/axiosInstance';

export const MONITOR_PAGE_SIZE = 50;

export const MONITOR_API_V1 = '/monitor/api/v1';

export const PIPELINE_LOGS_PATH = `${MONITOR_API_V1}/pipeline-logs` as const;

export type SearchType = 'EXACT' | 'LIKE';

export type PipelineLogsFilters = {
  pipelineKey?: string;
  pipelineName?: string;
  logMessage?: string;
  pipelineVersion?: string;
  logType?: string;
  level?: string;
  searchType?: SearchType;
  connectorId?: string;
  stepName?: string;
  agentExecutionKey?: string;
};

export type PipelineLogsRequest = {
  realm: string;
  environment: string;
  filters: PipelineLogsFilters;
  timeRange: {
    from: string;
    to: string;
  };
  pagination: {
    limit: number;
    order?: 'ascending' | 'descending';
    cursor?: string;
  };
};

export type PipelineLogAttributes = {
  'log.id': string;
  'log.type'?: string;
  'pipeline.name': string;
  'pipeline.key': string;
  'pipeline.version': string;
};

export type PipelineLogResource = {
  'service.name': string;
};

export type PipelineLogItem = {
  body: string;
  severityText: string;
  severityNumber: number;
  timestamp: string;
  observedTimestamp: string;
  attributes: PipelineLogAttributes;
  resource: PipelineLogResource;
  traceId: string | null;
  spanId: string | null;
};

export type PipelineLogsResponse = {
  data: PipelineLogItem[];
  pagination: {
    limit: number;
    nextCursor?: string;
    hasNext: boolean;
  };
  total: number;
};

export type PipelineLogOtel = {
  id: string;
  key: string;
  pipelineName: string;
  pipelineKey: string;
  pipelineVersion: string;
  logMessage: string;
  logType?: string;
  timestamp: string;
  observedTimestamp: string;
  severityText: string;
  severityNumber: number;
  serviceName: string;
  traceId: string | null;
  spanId: string | null;
};

export function transformOtelLog(
  log: PipelineLogItem,
  index: number
): PipelineLogOtel {
  const logId =
    log.attributes?.['log.id'] ?? `${log.timestamp}-${log.spanId ?? index}`;
  return {
    id: logId,
    key: logId,
    pipelineName: log.attributes?.['pipeline.name'] ?? '',
    pipelineKey: log.attributes?.['pipeline.key'] ?? '',
    pipelineVersion: log.attributes?.['pipeline.version'] ?? '',
    logMessage: log.body ?? '',
    logType: log.attributes?.['log.type'],
    timestamp: log.timestamp,
    observedTimestamp: log.observedTimestamp,
    severityText: log.severityText,
    severityNumber: log.severityNumber,
    serviceName: log.resource?.['service.name'] ?? '',
    traceId: log.traceId,
    spanId: log.spanId
  };
}

export type FetchPipelineLogsParams = {
  realm: string;
  environment: string;
  filters: PipelineLogsFilters;
  dateFrom: string;
  dateTo: string;
  limit: number;
  order?: 'ascending' | 'descending';
  cursor?: string;
};

export function buildPipelineLogsRequest({
  realm,
  environment,
  filters,
  dateFrom,
  dateTo,
  limit,
  order = 'descending',
  cursor
}: FetchPipelineLogsParams): PipelineLogsRequest {
  return {
    realm,
    environment,
    filters,
    timeRange: {
      from: new Date(dateFrom).toISOString(),
      to: new Date(dateTo).toISOString()
    },
    pagination: {
      limit,
      order,
      ...(cursor && { cursor })
    }
  };
}

export async function fetchPipelineLogs(
  params: FetchPipelineLogsParams
): Promise<PipelineLogsResponse> {
  const request = buildPipelineLogsRequest(params);

  const response = await axiosInstance.post<PipelineLogsResponse>(
    PIPELINE_LOGS_PATH,
    request,
    { params: { environment: params.environment } }
  );

  return response.data;
}

export const pipelineLogsQueryKeys = {
  all: ['pipeline-logs'] as const,
  lists: () => [...pipelineLogsQueryKeys.all, 'list'] as const,
  list: (params: {
    realm: string;
    environment: string;
    filters: PipelineLogsFilters;
    dateFrom: string;
    dateTo: string;
    limit: number;
    order?: string;
  }) => [...pipelineLogsQueryKeys.lists(), params] as const
};

// ============================================================================
// Executions API
// ============================================================================

export type ExecutionsFilters = {
  pipelineKey?: string;
  pipelineName?: string;
  pipelineVersion?: string;
  messageType?: string;
  payloadSearch?: string;
  source?: string;
  errorCode?: string;
  searchType?: SearchType;
};

export type ExecutionsRequest = {
  realm: string;
  environment: string;
  filters: ExecutionsFilters;
  timeRange: {
    from: string;
    to: string;
  };
  pagination: {
    limit: number;
    order?: 'ascending' | 'descending';
    cursor?: string;
    sortField?: 'timestamp' | 'elapsed_time';
  };
};

export type ExecutionOtelAttributes = {
  'log.type': string;
  'payload.response': string;
  'payload.request': string;
  'pipeline.source': string;
  'pipeline.name': string;
  'pipeline.key': string;
  'pipeline.version': string;
  'pipeline.id'?: string;
  'execution.request_size_bytes': number;
  'execution.response_size_bytes': number;
  'execution.elapsed_time_ms': number;
};

export type ExecutionOtelResource = {
  'service.name': string;
};

export type ExecutionOtelItem = {
  observedTimestamp: string;
  severityNumber: number;
  severityText: string;
  body: string;
  attributes: ExecutionOtelAttributes;
  resource: ExecutionOtelResource;
  traceId: string | null;
  spanId: string | null;
  timestamp: string;
};

export type ExecutionsResponse = {
  data: ExecutionOtelItem[];
  pagination: {
    limit: number;
    nextCursor?: string;
    hasNext: boolean;
  };
  total: number;
};

export type ExecutionOtel = {
  id: string;
  key: string;
  request: string;
  response: string;
  messageType: string;
  pipeline_name: string;
  pipeline_version: string;
  pipeline_source: string;
  pipeline_key: string;
  pipeline_id?: string;
  elapsed_time: number;
  request_timestamp: string;
  request_size_bytes: number;
  response_size_bytes: number;
  serviceName: string;
  traceId: string | null;
  spanId: string | null;
};

export function transformOtelExecution(item: ExecutionOtelItem): ExecutionOtel {
  return {
    id: item.attributes['pipeline.key'],
    key: item.attributes['pipeline.key'],
    request: item.attributes['payload.request'],
    response: item.attributes['payload.response'],
    messageType: item.attributes['log.type'],
    pipeline_name: item.attributes['pipeline.name'],
    pipeline_version: item.attributes['pipeline.version'],
    pipeline_source: item.attributes['pipeline.source'],
    pipeline_key: item.attributes['pipeline.key'],
    elapsed_time: item.attributes['execution.elapsed_time_ms'],
    request_timestamp: item.timestamp,
    request_size_bytes: item.attributes['execution.request_size_bytes'],
    response_size_bytes: item.attributes['execution.response_size_bytes'],
    serviceName: item.resource['service.name'],
    traceId: item.traceId,
    spanId: item.spanId,
    pipeline_id: item.attributes['pipeline.id']
  };
}

export type FetchExecutionsParams = {
  realm: string;
  environment: string;
  filters: ExecutionsFilters;
  dateFrom: string;
  dateTo: string;
  limit: number;
  order?: 'ascending' | 'descending';
  cursor?: string;
  sortField?: 'timestamp' | 'elapsed_time';
};

export function toExecutionsApiSortField(
  sortBy?: string | null
): 'elapsed_time' | undefined {
  return sortBy === 'elapsed_time' ? 'elapsed_time' : undefined;
}

export async function fetchExecutions({
  realm,
  environment,
  filters,
  dateFrom,
  dateTo,
  limit,
  order = 'descending',
  cursor,
  sortField
}: FetchExecutionsParams): Promise<ExecutionsResponse> {
  const request: ExecutionsRequest = {
    realm,
    environment,
    filters,
    timeRange: {
      from: new Date(dateFrom).toISOString(),
      to: new Date(dateTo).toISOString()
    },
    pagination: {
      limit,
      order,
      ...(cursor && { cursor }),
      ...(sortField === 'elapsed_time' && { sortField: 'elapsed_time' })
    }
  };

  const response = await axiosInstance.post<ExecutionsResponse>(
    `${MONITOR_API_V1}/executions`,
    request,
    { params: { environment } }
  );

  return response.data;
}

// ============================================================================
// Pipeline Logs Export API
// ============================================================================

export type ExportPipelineLogsRequest = {
  realm: string;
  environment: string;
  pipelineKey: string;
  separator?: string;
  timeRange: {
    from: string;
    to: string;
  };
};

export type ExportPipelineLogsParams = {
  realm: string;
  environment: string;
  pipelineKey: string;
  separator?: string;
  dateFrom: string;
  dateTo: string;
};

export async function exportPipelineLogs({
  realm,
  environment,
  pipelineKey,
  separator,
  dateFrom,
  dateTo
}: ExportPipelineLogsParams): Promise<Blob> {
  const request: ExportPipelineLogsRequest = {
    realm,
    environment,
    pipelineKey,
    ...(separator && { separator }),
    timeRange: {
      from: new Date(dateFrom).toISOString(),
      to: new Date(dateTo).toISOString()
    }
  };

  const response = await axiosInstance.post(
    `${MONITOR_API_V1}/pipeline-logs/export`,
    request,
    { params: { environment }, responseType: 'blob' }
  );

  return response.data;
}

// ============================================================================
// Pipeline Logs Count API
// ============================================================================

export type CountPipelineLogsParams = {
  realm: string;
  environment: string;
  pipelineKey: string;
  dateFrom: string;
  dateTo: string;
};

export async function countPipelineLogs({
  realm,
  environment,
  pipelineKey,
  dateFrom,
  dateTo
}: CountPipelineLogsParams): Promise<number> {
  const response = await axiosInstance.post<{ logCount: number }>(
    `${MONITOR_API_V1}/pipeline-logs/count`,
    {
      realm,
      environment,
      pipelineKey,
      timeRange: {
        from: new Date(dateFrom).toISOString(),
        to: new Date(dateTo).toISOString()
      }
    },
    { params: { environment } }
  );

  return response.data.logCount;
}

export const executionsQueryKeys = {
  all: ['executions'] as const,
  lists: () => [...executionsQueryKeys.all, 'list'] as const,
  list: (params: {
    realm: string;
    environment: string;
    filters: ExecutionsFilters;
    dateFrom: string;
    dateTo: string;
    limit: number;
    order?: string;
    sortField?: string;
  }) => [...executionsQueryKeys.lists(), params] as const
};

// ============================================================================
// Pipeline Intercept (Messages) API
// ============================================================================

export type PipelineInterceptRequest = {
  realm: string;
  environment: string;
  pipelineKey: string;
  timeRange: {
    from: string;
    to: string;
  };
  pagination: {
    limit: number;
    order?: 'ascending' | 'descending';
    cursor?: string;
  };
};

export type PipelineInterceptAttributes = {
  'log.type'?: string;
  'payload.response'?: string;
  'payload.request'?: string;
  'pipeline.name'?: string;
  'pipeline.key'?: string;
  'pipeline.version'?: string;
  'execution.request_size_bytes'?: number;
  'execution.response_size_bytes'?: number;
  'execution.elapsed_time_ms'?: number;
  error_code?: string;
  processor_type?: string;
  processor_step_name?: string;
  elapsed_time?: number;
  [key: string]: unknown;
};

export type PipelineInterceptItem = {
  body: string;
  severityText: string;
  severityNumber: number;
  timestamp: string;
  observedTimestamp: string;
  attributes: PipelineInterceptAttributes;
  resource?: {
    'service.name'?: string;
  };
  traceId: string | null;
  spanId: string | null;
};

export type PipelineInterceptResponse = {
  data: PipelineInterceptItem[];
  pagination: {
    limit: number;
    nextCursor?: string;
    hasNext: boolean;
  };
  total: number;
};

export type PipelineInterceptMessage = {
  id: string;
  body: string;
  elapsed: number;
  processorStepName: string;
  processorType: string;
  timestamp: string;
  severityText: string;
  severityNumber: number;
  traceId: string | null;
  spanId: string | null;
  pipelineKey: string;
  pipelineName: string;
  pipelineVersion: string;
};

export function transformInterceptMessage(
  item: PipelineInterceptItem,
  index: number
): PipelineInterceptMessage {
  const { attributes } = item;

  return {
    id: item.traceId ?? `${item.timestamp}-${index}`,
    body: item.body,
    elapsed:
      attributes.elapsed_time ?? attributes['execution.elapsed_time_ms'] ?? 0,
    processorStepName:
      attributes.processor_step_name ?? attributes['pipeline.name'] ?? '',
    processorType: attributes.processor_type ?? attributes['log.type'] ?? '',
    timestamp: item.timestamp,
    severityText: item.severityText,
    severityNumber: item.severityNumber,
    traceId: item.traceId,
    spanId: item.spanId,
    pipelineKey: attributes['pipeline.key'] ?? '',
    pipelineName: attributes['pipeline.name'] ?? '',
    pipelineVersion: attributes['pipeline.version'] ?? ''
  };
}

export type FetchPipelineInterceptParams = {
  realm: string;
  environment: string;
  pipelineKey: string;
  dateFrom: string;
  dateTo: string;
  limit: number;
  order?: 'ascending' | 'descending';
  cursor?: string;
};

export async function fetchPipelineIntercept({
  realm,
  environment,
  pipelineKey,
  dateFrom,
  dateTo,
  limit,
  order = 'descending',
  cursor
}: FetchPipelineInterceptParams): Promise<PipelineInterceptResponse> {
  const request: PipelineInterceptRequest = {
    realm,
    environment,
    pipelineKey,
    timeRange: {
      from: new Date(dateFrom).toISOString(),
      to: new Date(dateTo).toISOString()
    },
    pagination: {
      limit,
      order,
      ...(cursor && { cursor })
    }
  };

  const response = await axiosInstance.post<PipelineInterceptResponse>(
    `${MONITOR_API_V1}/pipeline-intercept`,
    request,
    { params: { environment } }
  );

  return response.data;
}

export const pipelineInterceptQueryKeys = {
  all: ['pipeline-intercept'] as const,
  lists: () => [...pipelineInterceptQueryKeys.all, 'list'] as const,
  list: (params: {
    realm: string;
    environment: string;
    pipelineKey: string;
    dateFrom: string;
    dateTo: string;
  }) => [...pipelineInterceptQueryKeys.lists(), params] as const
};

// ============================================================================
// Overview API
// ============================================================================

export type OverviewFilters = {
  pipelineName?: string;
  searchType?: SearchType;
};

export function buildOverviewFilters(
  pipelineName?: string | null,
  withSearchType = false
): OverviewFilters | undefined {
  if (!pipelineName) return undefined;

  if (!withSearchType) {
    return { pipelineName };
  }

  if (pipelineName.startsWith('*') && pipelineName.endsWith('*')) {
    const stripped = pipelineName.slice(1, -1);
    if (!stripped) return undefined;
    return { pipelineName: stripped, searchType: 'LIKE' };
  }

  return { pipelineName, searchType: 'EXACT' };
}

export type OverviewRequest = {
  realm: string;
  environment: string;
  percentile: string;
  filters?: OverviewFilters;
  timeRange: {
    from: string;
    to: string;
  };
  sort?: SortInput;
};

export type OverviewChartDataPoint = {
  tag: string;
  count: number;
  countSuccess: number;
  countError: number;
  min: number;
  max: number;
  avg: number;
  sum: number;
};

export type OverviewChartResponse = {
  data: OverviewChartDataPoint[];
  percentile: string;
};

export type OverviewTableDataRow = {
  pipeline_name: string;
  pipeline_version: string;
  pipeline_id: string;
  project_name: string;
  countSuccess: number;
  countError: number;
  elapsed_time: number;
};

export type OverviewTableResponse = {
  data: OverviewTableDataRow[];
  percentile: string;
};

export type FetchOverviewChartParams = {
  realm: string;
  environment: string;
  percentile: string;
  pipelineName?: string | null;
  dateFrom: string;
  dateTo: string;
  withSearchType?: boolean;
};

export async function fetchOverviewChart({
  realm,
  environment,
  percentile,
  pipelineName,
  dateFrom,
  dateTo,
  withSearchType
}: FetchOverviewChartParams): Promise<OverviewChartResponse> {
  const filters = buildOverviewFilters(pipelineName, withSearchType);
  const request: OverviewRequest = {
    realm,
    environment,
    percentile,
    timeRange: {
      from: dateFrom,
      to: dateTo
    },
    ...(filters && { filters })
  };

  const response = await axiosInstance.post<OverviewChartResponse>(
    `${MONITOR_API_V1}/overview/chart`,
    request,
    { params: { environment } }
  );

  return response.data;
}

export type FetchOverviewTableParams = {
  realm: string;
  environment: string;
  percentile: string;
  pipelineName?: string | null;
  dateFrom: string;
  dateTo: string;
  sort?: SortInput | null;
  withSearchType?: boolean;
};

export type SortInput = {
  sortBy: 'elapsed_time' | 'pipeline_name' | 'count_success' | 'count_error';
  sortOrder: 'ASC' | 'DESC';
};

export async function fetchOverviewTable({
  realm,
  environment,
  percentile,
  pipelineName,
  dateFrom,
  dateTo,
  sort,
  withSearchType
}: FetchOverviewTableParams): Promise<OverviewTableResponse> {
  const filters = buildOverviewFilters(pipelineName, withSearchType);
  const request: OverviewRequest = {
    realm,
    environment,
    percentile,
    timeRange: {
      from: dateFrom,
      to: dateTo
    },
    ...(filters && { filters }),
    ...(sort && { sort })
  };

  const response = await axiosInstance.post<OverviewTableResponse>(
    `${MONITOR_API_V1}/overview/table`,
    request,
    { params: { environment } }
  );

  return response.data;
}

// ============================================================================
// Pipelines and Version Autocomplete API
// ============================================================================

export type PipelinesAndVersionReturnType = 'pipeline' | 'version';

export type PipelinesAndVersionSearchParams = {
  realm: string;
  pipelineName?: string;
  versionMajor?: number;
  versionMinor?: number;
  returnType: PipelinesAndVersionReturnType;
  page?: number;
  limit?: number;
};

export type PipelinesAndVersionSearchResponse = {
  data: string[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
};

export async function fetchPipelinesAndVersionSearch({
  realm,
  pipelineName,
  versionMajor,
  versionMinor,
  returnType,
  page,
  limit
}: PipelinesAndVersionSearchParams): Promise<PipelinesAndVersionSearchResponse> {
  const params: Record<string, string | number> = {
    returnType: returnType.toUpperCase()
  };

  if (pipelineName !== undefined) params.pipelineName = pipelineName;
  if (versionMajor !== undefined) params.versionMajor = versionMajor;
  if (versionMinor !== undefined) params.versionMinor = versionMinor;
  if (page !== undefined) params.page = page;
  if (limit !== undefined) params.limit = limit;

  const response = await axiosInstance.get<PipelinesAndVersionSearchResponse>(
    `/design/realms/${realm}/pipelinesAndVersion/search`,
    { params }
  );

  return response.data;
}

export const pipelinesAndVersionQueryKeys = {
  all: ['pipelines-and-version'] as const,
  searches: () => [...pipelinesAndVersionQueryKeys.all, 'search'] as const,
  search: (params: {
    realm: string;
    query: string;
    returnType: PipelinesAndVersionReturnType;
    pipelineName?: string;
    versionMajor?: number;
    versionMinor?: number;
  }) => [...pipelinesAndVersionQueryKeys.searches(), params] as const
};

export const overviewQueryKeys = {
  all: ['monitor-overview'] as const,
  charts: () => [...overviewQueryKeys.all, 'chart'] as const,
  chart: (params: {
    realm: string;
    environment: string;
    percentile: string;
    pipelineName?: string | null;
    dateFrom: string;
    dateTo: string;
    withSearchType?: boolean;
  }) => [...overviewQueryKeys.charts(), params] as const,
  tables: () => [...overviewQueryKeys.all, 'table'] as const,
  table: (params: {
    realm: string;
    environment: string;
    percentile: string;
    pipelineName?: string | null;
    dateFrom: string;
    dateTo: string;
    sort?: SortInput | null;
    withSearchType?: boolean;
  }) => [...overviewQueryKeys.tables(), params] as const
};
